<template>
    <div class="pa-6 d-flex flex-column gap-6" data-testid="reporting-unit-details">
        <Breadcrumbs
            v-if="showCycleBreadcrumb"
            :glCodeText="breadcrumbCycleLabel"
            :glCodeInfo="loading ? '' : cycleBreadcrumbText"
            :activeCommentary="{}"
            :getStatusLabel="getStatusLabel"
            :isRefreshing="loading"
            @back="goBack"
            @refresh="fetchData(true)"
            data-testid="reporting-unit-details__breadcrumbs"
            :showRefresh="false"
        />

        <div v-if="loading" class="d-flex flex-column gap-4" data-testid="reporting-unit-details__skeleton">
            <div class="d-flex gap-4">
                <v-skeleton-loader type="chip" width="200" class="mr-2"></v-skeleton-loader>
                <div class="vertical"></div>
                <v-skeleton-loader type="chip" width="200" class="mr-2"></v-skeleton-loader>
                <div class="vertical"></div>
                <v-skeleton-loader type="chip" width="200"></v-skeleton-loader>
            </div>
             <div class="d-flex flex-column gap-3">
                <v-card v-for="i in 4" :key="i" class="d-flex flex-column pa-4 usercard gap-3" outlined>
                    <v-skeleton-loader type="text" width="150" class="mb-4"></v-skeleton-loader>
                    <div class="d-flex flex-wrap gap-3">
                        <v-skeleton-loader type="chip" width="200"></v-skeleton-loader>
                        <v-skeleton-loader type="chip" width="200"></v-skeleton-loader>
                    </div>
                </v-card>
            </div>
        </div>
        
        <div v-else class="d-flex flex-column gap-4" data-testid="reporting-unit-details__content">
            <div class="d-flex gap-4">
                <v-chip label class="chip-choose" data-testid="reporting-unit-details__network-chip">
                    <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'division_label', 'Network') }} - {{ detail.network_detail?.name }}</span>
                </v-chip>
                <div class="vertical"></div>
                <v-chip label class="chip-choose" data-testid="reporting-unit-details__region-chip">
                    <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'region_label', 'Region') }} - {{ detail.region_detail?.name }}</span>
                </v-chip>
                <div class="vertical"></div>
                <v-chip label class="chip-choose" data-testid="reporting-unit-details__country-chip">
                    <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'country_label', 'Country') }} - {{ detail.country_detail?.name }}</span>
                </v-chip>
            </div>

            <v-card class="pa-3 reportingcard d-flex flex-column"  data-testid="reporting-unit-details__card" rounded-xl evlevation="0">
                <v-tabs
                    v-model="tab"
                    background-color="transparent"
                    class="reporting-unit-details__tabs"
                    data-testid="reporting-unit-details__tabs"
                >
                    <v-tab
                        key="workflow"
                        data-testid="reporting-unit-details__tab--workflow"
                    >
                        Workflow
                    </v-tab>
                    <v-tab
                        key="userlist"
                        data-testid="reporting-unit-details__tab--userlist"
                    >
                        User List
                    </v-tab>
                </v-tabs>
                <v-divider></v-divider>
                <v-tabs-items v-model="tab" data-testid="reporting-unit-details__tabs-items">
                    <v-tab-item
                        key="workflow"
                        data-testid="reporting-unit-details__tab-item--workflow"
                    >
                        <ReportingUnitWorkflowTab class="pt-3" :detail="detail" />
                    </v-tab-item>
                    <v-tab-item
                        key="userlist"
                        data-testid="reporting-unit-details__tab-item--userlist"
                    >
                        <UserListTab class="pt-3" :detail="detail" />
                    </v-tab-item>
                </v-tabs-items>
            </v-card>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Breadcrumbs from '../../../components/Breadcrumbs.vue';
import ReportingUnitWorkflowTab from './ReportingUnitWorkflowTab.vue';
import UserListTab from '../../../components/UserListTab.vue';

export default {
    name: 'ReportingUnitDetails',
    components: {
        Breadcrumbs,
        ReportingUnitWorkflowTab,
        UserListTab
    },
    data() {
        return {
            loading: false,
            userId: '',
            showCycleBreadcrumb: true,
            breadcrumbCycleLabel: 'My Reporting Units', // Based on context
            cycleBreadcrumbText: '', 
            tab: null,
        }
    },
    computed: {
        ...mapGetters('varianceanalysis/reportingUnit', ['getRUDetail']),
        detail() {
            return this.getRUDetail || {};
        }
    },
    watch: {
        detail: {
            handler(val) {
                if (val && val.code) {
                    this.cycleBreadcrumbText = `${val.code} - ${val.name}`;
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.userId = this.$store.state.user.id;
        this.fetchData();
    },
    methods: {
        ...mapActions('varianceanalysis/reportingUnit', ['fetchRUDetail']),
        
        async fetchData(forceRefresh = false) {
            const id = this.$route.params.id;
            if (!id) return;

            this.loading = true;
            try {
                // Using existing action which accepts filter object
                await this.fetchRUDetail({
                    filter: { reportingunit: [id] },
                    axiosInstance: this.axios,
                    forceRefresh: forceRefresh === true
                });
            } catch (error) {
                console.error("Failed to fetch RU details", error);
            } finally {
                this.loading = false;
            }
        },
        getStatusLabel(key, fallback) {
            return fallback || key;
        },
        goBack() {
             this.$nova.gotoLink({
                path: '/varianceanalysis/myreportingunits'
            });
        }
    }
}
</script>

<style scoped>
.reporting-unit-details__tabs .v-tab {
    text-transform: none;
    font-weight: 400;
    font-size: 16px;
    color: var(--color-primary-base) !important;
    padding: var(--space-2xs) var(--space-md);
}
.reporting-unit-details__tabs .v-tab--active {
    color: var(--color-primary-base) !important;
    font-weight: 600;
}
::v-deep .reporting-unit-details__tabs .v-tabs-bar {
    height: 34px !important ;
}
</style>
